import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AddAltEmailPayload, AltAccInfo } from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { ChangeAlternateEmail, ChangePassword, ConfirmAlternatePassword } from '@common/types';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AlternateAccountService {
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly backupUsers = this.odataFactory.CreateService<any>('BackupUsers');

  getInfo(userId: string): Observable<AltAccInfo> {
    return this.backupUsers.CustomFunction(userId, 'GetAlternateEmail').pipe(map((res: HttpResponse<AltAccInfo>) => res.body));
  }

  addEmail(userId: string, payload: AddAltEmailPayload): Observable<any> {
    return this.backupUsers.CustomAction(userId, 'AddAlternateEmail', payload).pipe(
      map((res: HttpResponse<{ body: any }>) => res.body),
      catchError((res: HttpErrorResponse) => {
        const error = res && (res.error as { value: string }).value;
        const errorMessage = error || 'Error adding alternate Email';

        return throwError(() => errorMessage);
      })
    );
  }

  updateEmail(userId: string, email: string, password: string): Observable<any> {
    const payload: ChangeAlternateEmail = {
      Email: email,
      Password: password
    };

    return this.backupUsers.CustomAction(userId, 'EditAlternateEmail', payload).pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }

  deleteEmail(userId: string, password?: string) {
    const payload = password ? { Password: password } : {};

    return this.backupUsers.CustomAction(userId, 'DeleteAlternateEmail', payload).pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }

  updatePassword(userId: string, password: string, newPassword: string, confirmPassword: string) {
    const payload = {
      Password: password,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword
    } as ChangePassword;

    return this.backupUsers
      .CustomAction(userId, 'EditAlternatePassword', payload)
      .pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }

  confirmPassword(token: string, newPassword: string) {
    const payload = {
      NewPassword: newPassword,
      Token: token
    } as ConfirmAlternatePassword;

    return this.backupUsers
      .CustomCollectionAction('ConfirmAlternatePassword', payload)
      .pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }

  getTfaSecret(userId: string): Observable<string> {
    return this.backupUsers.CustomFunction(userId, 'GetTwoStepSecret').pipe(map((res: HttpResponse<{ value: string }>) => res.body.value));
  }

  enableTfa(userId: string, password: string, responseCode: string): Observable<any> {
    const payload = {
      Password: password,
      ResponseCode: responseCode
    };

    return this.backupUsers
      .CustomAction(userId, 'EnableAlternateTwoStep', payload)
      .pipe(map((res: HttpResponse<{ bode: any }>) => res.body));
  }

  disableTfa(userId: string, password: string): Observable<any> {
    const payload = { Password: password };

    return this.backupUsers
      .CustomAction(userId, 'DisableAlternateTwoStep', payload)
      .pipe(map((res: HttpResponse<{ body: any }>) => res.body));
  }
}
